// Assistant dock (DOCK-01): one side panel for Plutus (AI trading coach) and Support (help + a person when needed).
// Mounted once at the app root (App.js) for logged-in /app/* pages, so it stays open with the same conversation across
// page changes, including pages outside the Dashboard shell (Billing, Recap, profiles).
//  - >= 1200px on pages with the shell's <AssistantDockSpacer/>: docked, the page narrows so nothing is covered.
//  - 600-1199px: slides over the right edge; the page stays visible on the left.
//  - < 600px: full-height sheet.
// Open state + tab persist per browser. Ctrl/Cmd+J toggles, Esc closes. Other code opens it with openAssistant(tab).
// Hidden on the full Plutus (/app/ai-chat) and Support (/app/support) pages.
import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Box, ButtonBase, IconButton, Tooltip, Typography } from '@mui/material';
import { Link, useLocation } from 'react-router-dom';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import EditNoteRoundedIcon from '@mui/icons-material/EditNoteRounded';
import ArrowUpwardRoundedIcon from '@mui/icons-material/ArrowUpwardRounded';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import { SYSTEM_STACK } from '../journalDesign';
import usePlutusChat from './usePlutusChat';
import useSupportChat, { supportBanner, agentTyping } from '../support/useSupportChat';
import { PageLink, SupportImages } from '../support/supportUi';

export const DOCK_WIDTH = 400;
const DOCK_MIN_DOCKED = 1200;
const PHONE_MAX = 600;
const STORE_KEY = 'nummius.assistant';
export const ASSISTANT_OPEN_EVENT = 'nummius:assistant-open';
const HIDDEN_ON = ['/app/ai-chat', '/app/support'];
const EASE = 'cubic-bezier(.2,.8,.2,1)';

/** Open the dock from anywhere (e.g. the chart's Plutus pill): tab 'plutus' | 'support'. */
export function openAssistant(tab) {
  window.dispatchEvent(new CustomEvent(ASSISTANT_OPEN_EVENT, { detail: { tab } }));
}

const readStore = () => {
  try { return JSON.parse(localStorage.getItem(STORE_KEY)) || {}; } catch (e) { return {}; }
};
const writeStore = (v) => {
  try { localStorage.setItem(STORE_KEY, JSON.stringify(v)); } catch (e) { /* private mode */ }
};

// Marketing-site palette for dark (near-black glass, hairlines, soft blue), Apple light equivalent for light.
const tokens = (dark) => (dark ? {
  glass: 'rgba(14,16,19,0.86)', solid: '#0f1114', line: 'rgba(255,255,255,0.07)', line2: '#272b32',
  ink: '#eceef1', ink2: '#a8adb5', ink3: '#6a707a', field: '#14171b', fieldLine: '#272b32',
  ai: '#16191e', aiLine: '#22262c', accent: '#79a6ff', accentSolid: '#0a84ff', seg: '#14171b', segOn: '#272b32',
  human: 'rgba(142,92,247,0.16)', humanLine: 'rgba(142,92,247,0.38)', banner: '#14171b', warn: 'rgba(216,167,72,0.14)',
  shadow: '-18px 0 48px rgba(0,0,0,0.45)', launcher: 'rgba(20,23,27,0.92)',
} : {
  glass: 'rgba(255,255,255,0.86)', solid: '#ffffff', line: 'rgba(0,0,0,0.08)', line2: '#e5e5ea',
  ink: '#1d1d1f', ink2: '#6e6e73', ink3: '#86868b', field: '#f5f5f7', fieldLine: '#e5e5ea',
  ai: '#f2f2f7', aiLine: 'transparent', accent: '#0a84ff', accentSolid: '#0a84ff', seg: '#ececf0', segOn: '#ffffff',
  human: 'rgba(142,92,247,0.10)', humanLine: 'rgba(142,92,247,0.30)', banner: '#f5f5f7', warn: 'rgba(255,159,10,0.12)',
  shadow: '-18px 0 48px rgba(0,0,0,0.10)', launcher: 'rgba(255,255,255,0.92)',
});

/** Light/dark from MUI's <html data-mui-color-scheme> (set by AppTheme), so the dock matches every page, even outside it. */
function useHtmlDark() {
  const read = () => {
    const a = document.documentElement.getAttribute('data-mui-color-scheme');
    if (a === 'dark' || a === 'light') return a === 'dark';
    return !!(window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches);
  };
  const [dark, setDark] = useState(read);
  useEffect(() => {
    const mo = new MutationObserver(() => setDark(read()));
    mo.observe(document.documentElement, { attributes: true, attributeFilter: ['data-mui-color-scheme'] });
    return () => mo.disconnect();
  }, []);
  return dark;
}

function useViewport() {
  const get = () => (typeof window === 'undefined' ? 1440 : window.innerWidth);
  const [w, setW] = useState(get);
  useEffect(() => {
    const on = () => setW(get());
    window.addEventListener('resize', on);
    return () => window.removeEventListener('resize', on);
  }, []);
  return w;
}

const DockCtx = createContext(null);

export function AssistantDockProvider({ children, enabled = true }) {
  const stored = useMemo(readStore, []);
  const [open, setOpen] = useState(!!stored.open);
  const [tab, setTab] = useState(stored.tab === 'support' ? 'support' : 'plutus');
  const width = useViewport();
  const { pathname } = useLocation();
  const hidden = !enabled || HIDDEN_ON.some((p) => pathname === p || pathname.startsWith(p + '/'));
  const [spacers, setSpacers] = useState(0);   // docked only where the page shell can make room
  const registerSpacer = useCallback(() => {
    setSpacers((n) => n + 1);
    return () => setSpacers((n) => n - 1);
  }, []);
  const mode = width < PHONE_MAX ? 'sheet' : width >= DOCK_MIN_DOCKED && spacers > 0 ? 'docked' : 'overlay';

  useEffect(() => { writeStore({ open, tab }); }, [open, tab]);
  useEffect(() => {
    const onOpen = (e) => {
      const t = e.detail && e.detail.tab;
      if (t === 'plutus' || t === 'support') setTab(t);
      setOpen(true);
    };
    const onKey = (e) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && (e.key === 'j' || e.key === 'J')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener(ASSISTANT_OPEN_EVENT, onOpen);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener(ASSISTANT_OPEN_EVENT, onOpen);
      window.removeEventListener('keydown', onKey);
    };
  }, []);

  const value = useMemo(() => ({ open: open && !hidden, setOpen, tab, setTab, mode, hidden, registerSpacer }),
    [open, tab, mode, hidden, registerSpacer]);
  return <DockCtx.Provider value={value}>{children}</DockCtx.Provider>;
}

const useDock = () => useContext(DockCtx);

/** Flex-row sibling of the page's <main>: takes the panel's width while docked, so the page narrows instead of hiding. */
export function AssistantDockSpacer() {
  const d = useDock();
  const register = d && d.registerSpacer;
  useEffect(() => (register ? register() : undefined), [register]);
  const on = d && d.open && d.mode === 'docked';
  return <Box aria-hidden sx={{ width: on ? DOCK_WIDTH : 0, flexShrink: 0, transition: `width 220ms ${EASE}` }} />;
}

// ── pieces ────────────────────────────────────────────────────────────────

function Bubble({ K, from, children, links, images, label }) {
  const mine = from === 'me';
  const human = from === 'human';
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: mine ? 'flex-end' : 'flex-start', mb: '10px' }}>
      {label && <Typography sx={{ fontSize: 11, color: K.ink3, mb: '3px', px: '4px', fontFamily: SYSTEM_STACK }}>{label}</Typography>}
      <Box sx={{
        maxWidth: '86%', px: '13px', py: '8px', borderRadius: '18px', fontSize: 14, lineHeight: 1.45,
        whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontFamily: SYSTEM_STACK,
        bgcolor: mine ? K.accentSolid : human ? K.human : from === 'warn' ? K.warn : K.ai,
        color: mine ? '#fff' : K.ink,
        border: `1px solid ${mine ? 'transparent' : human ? K.humanLine : K.aiLine}`,
        borderBottomRightRadius: mine ? '6px' : '18px', borderBottomLeftRadius: mine ? '18px' : '6px',
      }}>
        {children}
      </Box>
      <SupportImages images={images} max="86%" />
      {links && links.length > 0 && (
        <Box sx={{ display: 'flex', gap: '6px', flexWrap: 'wrap', mt: '6px', maxWidth: '86%' }}>
          {links.map((l) => <PageLink key={l.url} link={l} />)}
        </Box>
      )}
    </Box>
  );
}

function Typing({ K, label }) {
  return (
    <Box role="status" sx={{ display: 'flex', alignItems: 'center', gap: '8px', mb: '10px', color: K.ink3, fontSize: 12, fontFamily: SYSTEM_STACK }}>
      <Box sx={{ display: 'flex', gap: '3px', px: '12px', py: '10px', borderRadius: '18px', bgcolor: K.ai,
        '@keyframes ndot': { '0%,80%,100%': { opacity: 0.25 }, '40%': { opacity: 1 } },
        '& span': { width: 6, height: 6, borderRadius: '50%', bgcolor: K.ink3, animation: 'ndot 1.2s infinite ease-in-out' },
        '& span:nth-of-type(2)': { animationDelay: '.15s' }, '& span:nth-of-type(3)': { animationDelay: '.3s' },
        '@media (prefers-reduced-motion: reduce)': { '& span': { animation: 'none', opacity: 0.6 } } }}>
        <span /><span /><span />
      </Box>
      {label}
    </Box>
  );
}

function Empty({ K, title, text, suggestions, onPick }) {
  return (
    <Box sx={{ textAlign: 'center', pt: '12vh', px: '12px', fontFamily: SYSTEM_STACK }}>
      <Typography sx={{ fontSize: 20, fontWeight: 650, letterSpacing: '-0.02em', color: K.ink, fontFamily: SYSTEM_STACK }}>{title}</Typography>
      <Typography sx={{ fontSize: 13.5, color: K.ink2, mt: '6px', mb: '18px', lineHeight: 1.5, fontFamily: SYSTEM_STACK }}>{text}</Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '8px', alignItems: 'stretch' }}>
        {suggestions.map((s) => (
          <ButtonBase key={s} onClick={() => onPick(s)}
            sx={{ justifyContent: 'flex-start', textAlign: 'left', px: '14px', py: '10px', borderRadius: '12px', fontSize: 13.5,
              fontFamily: SYSTEM_STACK, color: K.ink, border: `1px solid ${K.line2}`, bgcolor: 'transparent',
              transition: 'background-color .15s ease-out', '&:hover': { bgcolor: K.field } }}>
            {s}
          </ButtonBase>
        ))}
      </Box>
    </Box>
  );
}

function Composer({ K, placeholder, disabled, onSend, hint, autoFocusKey }) {
  const [text, setText] = useState('');
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 140) + 'px';
  }, [text]);
  useEffect(() => { if (autoFocusKey && ref.current) ref.current.focus({ preventScroll: true }); }, [autoFocusKey]);
  const submit = async () => {
    const t = text.trim();
    if (!t || disabled) return;
    setText('');
    const ok = await onSend(t);
    if (ok === false) setText(t);
  };
  return (
    <Box sx={{ px: '14px', pt: '10px', pb: '12px', borderTop: `1px solid ${K.line}` }}>
      <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '8px', bgcolor: K.field, border: `1px solid ${K.fieldLine}`,
        borderRadius: '20px', pl: '14px', pr: '5px', py: '5px', transition: 'border-color .15s ease-out',
        '&:focus-within': { borderColor: K.accent } }}>
        <Box component="textarea" ref={ref} rows={1} value={text} maxLength={4000} placeholder={placeholder}
          aria-label={placeholder} onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing) { e.preventDefault(); submit(); } }}
          sx={{ flex: 1, resize: 'none', border: 'none', outline: 'none', bgcolor: 'transparent', color: K.ink,
            font: 'inherit', fontFamily: SYSTEM_STACK, fontSize: 14, lineHeight: '20px', py: '6px', maxHeight: 140,
            '&::placeholder': { color: K.ink3 } }} />
        <IconButton aria-label="Send" onClick={submit} disabled={disabled || !text.trim()}
          sx={{ width: 32, height: 32, bgcolor: K.accentSolid, color: '#fff', transition: 'opacity .15s ease-out',
            '&:hover': { bgcolor: K.accentSolid, opacity: 0.88 }, '&.Mui-disabled': { bgcolor: K.line2, color: K.ink3 } }}>
          <ArrowUpwardRoundedIcon sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
      {hint && <Typography sx={{ fontSize: 11, color: K.ink3, mt: '6px', px: '4px', fontFamily: SYSTEM_STACK }}>{hint}</Typography>}
    </Box>
  );
}

function useAutoScroll(dep) {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (el) el.scrollTo({ top: el.scrollHeight, behavior: 'smooth' });
  }, [dep]);
  return ref;
}

// ── tabs ──────────────────────────────────────────────────────────────────

const PLUTUS_SUGGESTIONS = ["What's my most costly pattern?", 'Which playbook actually works?', 'What mistakes do I keep repeating?'];
const SUPPORT_SUGGESTIONS = ['How do I reset my password?', 'Is there a free plan?', 'How do I connect my broker?'];

function PlutusPane({ K, plutus, focusKey }) {
  const { messages, loading, usage, showNudge, send } = plutus;
  const scroll = useAutoScroll(messages.length + (loading ? 1 : 0));
  return (
    <>
      {showNudge && (
        <Box sx={{ mx: '14px', mt: '10px', px: '12px', py: '8px', borderRadius: '10px', bgcolor: K.banner, fontSize: 12, color: K.ink2, fontFamily: SYSTEM_STACK }}>
          {usage.reviewed_trade_count} of {usage.min_reviewed_trades} trades reviewed: more reviews, sharper answers.
        </Box>
      )}
      <Box ref={scroll} sx={{ flex: 1, overflowY: 'auto', px: '14px', pt: '14px' }} aria-live="polite">
        {messages.length === 0 && !loading && (
          <Empty K={K} title="Ask Plutus" text="Your AI trading coach. It knows your journal, trades and playbooks."
            suggestions={PLUTUS_SUGGESTIONS} onPick={send} />
        )}
        {messages.map((m, i) => (
          <Bubble key={i} K={K} from={m.role === 'user' ? 'me' : m.capReached || m.error ? 'warn' : 'ai'}>{m.content}</Bubble>
        ))}
        {loading && <Typing K={K} label="Plutus is thinking…" />}
      </Box>
      <Composer K={K} placeholder="Ask Plutus…" disabled={loading} onSend={(t) => { send(t); return true; }}
        hint={usage && usage.cap ? `${usage.used ?? 0} of ${usage.cap} messages today` : 'Not financial advice.'}
        autoFocusKey={focusKey} />
    </>
  );
}

function SupportPane({ K, support, focusKey }) {
  const { conv, pending, busy, error, state } = support;
  const msgs = conv ? conv.messages : [];
  const scroll = useAutoScroll(msgs.length + (pending ? 1 : 0));
  const label = (m) => (m.sender_type === 'human' ? 'Support team · a person' : null);
  return (
    <>
      {support.statusNote && (
        <Box role="status" sx={{ mx: '14px', mt: '10px', px: '12px', py: '8px', borderRadius: '10px', fontSize: 12.5, lineHeight: 1.45,
          bgcolor: 'rgba(255,159,10,0.16)', color: K.ink, fontFamily: SYSTEM_STACK }}>
          ⚠️ {support.statusNote.text}
        </Box>
      )}
      {supportBanner(conv) && (
        <Box role="status" sx={{ mx: '14px', mt: '10px', px: '12px', py: '8px', borderRadius: '10px', fontSize: 12.5, lineHeight: 1.45,
          bgcolor: state === 'HUMAN_ACTIVE' ? K.human : K.banner, color: K.ink, fontFamily: SYSTEM_STACK }}>
          {supportBanner(conv)}
        </Box>
      )}
      <Box ref={scroll} sx={{ flex: 1, overflowY: 'auto', px: '14px', pt: '14px' }} aria-live="polite">
        {!conv && !pending && (
          <Empty K={K} title="How can we help?" text="Answers from our help center, and a person from the team whenever you need one."
            suggestions={SUPPORT_SUGGESTIONS} onPick={support.send} />
        )}
        {msgs.map((m) => (m.sender_type === 'system'
          ? <Typography key={m.id} sx={{ textAlign: 'center', fontSize: 11.5, color: K.ink3, my: '10px', px: '10px', fontFamily: SYSTEM_STACK }}>{m.content}</Typography>
          : <Bubble key={m.id} K={K} from={m.sender_type === 'customer' ? 'me' : m.sender_type === 'human' ? 'human' : 'ai'}
              links={m.links} images={m.images} label={label(m)}>{m.content}</Bubble>))}
        {pending && <Bubble K={K} from="me">{pending}</Bubble>}
        {!pending && agentTyping(conv) && <Typing K={K} label={`${agentTyping(conv)} is typing…`} />}
        {pending && (!conv || state === 'AI_ACTIVE' || state === 'RESOLVED') && <Typing K={K} label="Support assistant is writing…" />}
        {support.canRate && (
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px', my: '12px' }}>
            <Typography sx={{ fontSize: 12.5, color: K.ink3, fontFamily: SYSTEM_STACK }}>
              {conv.rating ? 'Thanks for the feedback!' : 'Did we help?'}
            </Typography>
            {[[1, '👍', 'Yes, this helped'], [-1, '👎', 'No, this didn’t help']].map(([v, icon, label]) => (
              <Chip key={v} K={K} aria-label={label} aria-pressed={conv.rating === v} onClick={() => support.rate(v)}
                sx={conv.rating === v ? { outline: `2px solid ${K.ink}` } : undefined}>{icon}</Chip>
            ))}
          </Box>
        )}
        {error && <Typography role="alert" sx={{ fontSize: 12.5, color: '#ff6b6b', mb: '10px', fontFamily: SYSTEM_STACK }}>{error}</Typography>}
      </Box>
      {(support.canAskHuman || support.canResolve) && (
        <Box sx={{ display: 'flex', gap: '6px', px: '14px', pt: '8px' }}>
          {support.canAskHuman && <Chip K={K} onClick={support.requestHuman} disabled={busy}>Talk to a person</Chip>}
          {support.canResolve && <Chip K={K} onClick={support.resolve} disabled={busy}>Mark resolved</Chip>}
        </Box>
      )}
      <Composer K={K} disabled={busy}
        placeholder={state === 'CLOSED' ? 'Write to start a new conversation…' : 'Message support…'}
        onSend={support.send} hint="Never share passwords or card numbers in chat." autoFocusKey={focusKey} />
    </>
  );
}

function Chip({ K, children, sx, ...rest }) {
  return (
    <ButtonBase {...rest} sx={{ ...sx, px: '11px', py: '5px', borderRadius: '14px', fontSize: 12, fontWeight: 550, fontFamily: SYSTEM_STACK,
      color: K.ink, border: `1px solid ${K.line2}`, transition: 'background-color .15s ease-out',
      '&:hover': { bgcolor: K.field }, '&.Mui-disabled': { opacity: 0.5 } }}>
      {children}
    </ButtonBase>
  );
}

// ── panel + launcher ──────────────────────────────────────────────────────

export default function AssistantDock() {
  const d = useDock();
  const dark = useHtmlDark();
  const K = tokens(dark);
  const panelRef = useRef(null);
  const open = d.open;
  const tab = d.tab;
  const plutus = usePlutusChat(open && tab === 'plutus');
  // Support loads once on mount (one light request) and then polls only while its tab is open, or while a person
  // is involved in the open conversation, so a team reply shows as unread even when the dock is closed.
  const [humanState, setHumanState] = useState(false);
  const [booted, setBooted] = useState(false);
  const support = useSupportChat(!d.hidden && ((open && tab === 'support') || humanState || !booted));
  useEffect(() => { if (support.list !== null) setBooted(true); }, [support.list]);
  useEffect(() => { setHumanState(support.state === 'HUMAN_REQUESTED' || support.state === 'HUMAN_ACTIVE'); }, [support.state]);

  // unread: newest support message from the team/assistant arrived while the Support tab wasn't visible
  const viewingSupport = open && tab === 'support';
  const lastMsg = support.conv && support.conv.messages.length ? support.conv.messages[support.conv.messages.length - 1] : null;
  const [seenId, setSeenId] = useState(0);
  useEffect(() => { if (viewingSupport && lastMsg) setSeenId(lastMsg.id); }, [viewingSupport, lastMsg]);
  const unread = !!lastMsg && lastMsg.sender_type === 'human' && lastMsg.id > seenId && !viewingSupport;

  const [focusKey, setFocusKey] = useState(0);
  useEffect(() => { if (open) setFocusKey((k) => k + 1); }, [open, tab]);

  const close = useCallback(() => d.setOpen(false), [d]);
  // Esc closes while focus is in the panel or nowhere in particular (e.g. after a button inside it disappeared);
  // never while the user is typing in the page or a dialog is open.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      const a = document.activeElement;
      const inPanel = panelRef.current && a && panelRef.current.contains(a);
      if (inPanel || !a || a === document.body) {
        if (document.querySelector('[role="dialog"]')) return;
        close();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, close]);

  if (d.hidden) return null;
  const sheet = d.mode === 'sheet';
  const fullHref = tab === 'plutus'
    ? (plutus.conversationId ? `/app/ai-chat?c=${plutus.conversationId}` : '/app/ai-chat') : '/app/support';

  return (
    <>
      <Box ref={panelRef} component="aside" role="complementary" aria-label="Assistant" aria-hidden={!open}
        sx={{
          position: 'fixed', top: 0, right: 0, bottom: 0, width: sheet ? '100vw' : DOCK_WIDTH, zIndex: 1250,
          display: 'flex', flexDirection: 'column', fontFamily: SYSTEM_STACK, color: K.ink,
          // glass beside a visible page; solid as a full-screen phone sheet (nothing worth seeing through it)
          bgcolor: sheet ? K.solid : K.glass, backdropFilter: 'blur(18px) saturate(160%)', WebkitBackdropFilter: 'blur(18px) saturate(160%)',
          borderLeft: `1px solid ${K.line}`, boxShadow: d.mode === 'docked' ? 'none' : K.shadow,
          transform: open ? 'translateX(0)' : 'translateX(100%)',
          visibility: open ? 'visible' : 'hidden',
          transition: open ? `transform 240ms ${EASE}, visibility 0s` : `transform 200ms ${EASE}, visibility 0s 200ms`,
          '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
          '@supports not ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px)))': { bgcolor: K.solid },
        }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px', px: '12px', height: 56, flexShrink: 0, borderBottom: `1px solid ${K.line}` }}>
          <Box role="tablist" aria-label="Assistant" sx={{ display: 'flex', bgcolor: K.seg, borderRadius: '10px', p: '2px', flex: 1, maxWidth: 236 }}>
            {[['plutus', 'Plutus'], ['support', 'Support']].map(([id, name]) => {
              const on = tab === id;
              return (
                <ButtonBase key={id} role="tab" aria-selected={on} onClick={() => d.setTab(id)}
                  sx={{ flex: 1, py: '6px', borderRadius: '8px', fontSize: 13, fontWeight: on ? 600 : 500, fontFamily: SYSTEM_STACK,
                    color: on ? K.ink : K.ink2, bgcolor: on ? K.segOn : 'transparent', gap: '6px',
                    boxShadow: on ? '0 1px 2px rgba(0,0,0,0.12)' : 'none', transition: 'all .18s ease-out' }}>
                  {name}
                  {id === 'support' && unread && <Box component="span" aria-label="new reply" sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: '#8e5cf7' }} />}
                </ButtonBase>
              );
            })}
          </Box>
          <Box sx={{ flex: 1 }} />
          <Tooltip title={tab === 'plutus' ? 'New chat' : 'New conversation'}>
            <IconButton aria-label={tab === 'plutus' ? 'New chat' : 'New conversation'} size="small" sx={{ color: K.ink2 }}
              onClick={tab === 'plutus' ? plutus.newChat : support.newConversation}>
              <EditNoteRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          <Tooltip title="Open full page">
            <IconButton aria-label="Open full page" size="small" component={Link} to={fullHref} sx={{ color: K.ink2 }}
              onClick={() => { if (d.mode !== 'docked') close(); }}>
              <OpenInNewRoundedIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="Close (Esc)">
            <IconButton aria-label="Close assistant" size="small" onClick={close} sx={{ color: K.ink2 }}>
              <CloseRoundedIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
        <Typography sx={{ fontSize: 11.5, color: K.ink3, px: '16px', pt: '8px', fontFamily: SYSTEM_STACK }}>
          {tab === 'plutus' ? 'AI trading coach · knows your journal' : 'Help center answers · a person when needed'}
        </Typography>
        {tab === 'plutus'
          ? <PlutusPane K={K} plutus={plutus} focusKey={open && tab === 'plutus' ? focusKey : 0} />
          : <SupportPane K={K} support={support} focusKey={open && tab === 'support' ? focusKey : 0} />}
      </Box>

      {!open && (
        <Tooltip title="Plutus & Support (Ctrl+J)" placement="left">
          <ButtonBase aria-label="Open assistant: Plutus and Support" onClick={() => d.setOpen(true)}
            sx={{ position: 'fixed', right: 24, bottom: 96, zIndex: 1250, width: 52, height: 52, borderRadius: '50%',
              bgcolor: K.launcher, color: K.accent, border: `1px solid ${K.line2}`,
              backdropFilter: 'blur(18px)', WebkitBackdropFilter: 'blur(18px)',
              boxShadow: dark ? '0 8px 28px rgba(0,0,0,0.5)' : '0 8px 28px rgba(0,0,0,0.14)',
              transition: 'transform .18s ease-out', '&:hover': { transform: 'scale(1.05)' },
              '@media (prefers-reduced-motion: reduce)': { transition: 'none', '&:hover': { transform: 'none' } } }}>
            <AutoAwesomeRoundedIcon sx={{ fontSize: 22 }} />
            {unread && <Box component="span" sx={{ position: 'absolute', top: 4, right: 4, width: 10, height: 10, borderRadius: '50%',
              bgcolor: '#8e5cf7', border: `2px solid ${K.solid}` }} />}
          </ButtonBase>
        </Tooltip>
      )}
    </>
  );
}
